import React, { useContext } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  Icon,
} from '@chakra-ui/react';
import { FaExclamationTriangle, FaHome } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import ThemeContext from '../context/ThemeContext';

function NotFound() {
  const navigate = useNavigate();
  const { themeData } = useContext(ThemeContext);

  // Extract theme colors with fallbacks
  const primaryColor = themeData?.colors?.primary || '#2B6CB0';
  const secondaryColor = themeData?.colors?.secondary || '#2C5282';
  const accentColor = themeData?.colors?.accent || '#63B3ED';
  // const backgroundColor = themeData?.colors?.background || '#F7FAFC';
  const textColor = themeData?.colors?.text || '#1A202C';

  // Determine if theme is dark (midnight)
  const isDarkTheme = themeData?.id === 'midnight';

  // Icon background gradient - lighter shades for both themes
  const iconBgGradient = isDarkTheme
    ? `linear(to-r, ${secondaryColor}, ${primaryColor})`
    : `linear(to-r, ${accentColor}40, ${primaryColor}20)`;

  // Icon color
  const iconColor = isDarkTheme ? accentColor : primaryColor;

  // 404 heading gradient
  const headingGradient = `linear(to-r, ${primaryColor}, ${accentColor})`;

  // Main heading color
  const mainHeadingColor = isDarkTheme ? textColor : 'gray.700';

  // Description text color
  const descriptionColor = isDarkTheme ? textColor : 'gray.600';

  // Button gradient
  const buttonGradient = `linear(to-r, ${primaryColor}, ${accentColor})`;
  const buttonHoverGradient = isDarkTheme
    ? `linear(to-r, ${secondaryColor}, ${primaryColor})`
    : `linear(to-r, ${primaryColor}dd, ${accentColor}dd)`;

  return (
    <Container maxW="container.xl" py={20}>
      <VStack spacing={8} align="center" textAlign="center">
        {/* Icon */}
        <Box
          p={8}
          borderRadius="full"
          bgGradient={iconBgGradient}
        >
          <Icon 
            as={FaExclamationTriangle} 
            boxSize={24} 
            color={iconColor}
          />
        </Box>

        {/* 404 Text */}
        <Heading
          size="4xl"
          bgGradient={headingGradient}
          bgClip="text"
          fontWeight="extrabold"
        >
          404
        </Heading>

        {/* Main Message */}
        <VStack spacing={3}>
          <Heading size="xl" color={mainHeadingColor}>
            Page Not Found
          </Heading>
          <Text color={descriptionColor} fontSize="lg" maxW="md">
            Oops! The page you're looking for doesn't exist. It might have been moved or deleted.
          </Text>
        </VStack>

        {/* Action Buttons */}
        <VStack spacing={3} pt={4}>
          <Button
            leftIcon={<FaHome />}
            size="lg"
            bgGradient={buttonGradient}
            color="white"
            onClick={() => navigate('/')}
            px={8}
            _hover={{
              bgGradient: buttonHoverGradient,
            }}
          >
            Go to Homepage
          </Button>
          <Button
            variant="ghost"
            color={primaryColor}
            onClick={() => navigate(-1)}
            _hover={{
              bg: isDarkTheme ? secondaryColor : `${accentColor}20`,
            }}
          >
            Go Back
          </Button>
        </VStack>
      </VStack>
    </Container>
  );
}

export default NotFound;